import { Injectable } from '@angular/core';
import { ApiClient, BffUserService } from '@canvas/four-r-digital';
import { ProductType } from '../dtos';
import { BehaviorSubject, combineLatest, filter, map, take } from 'rxjs';
import { productTypeUrl } from '../ApiKeys';

interface RolesState {
  rolesForProduct: string[];
  activeRole: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class RolesService {
  private rolesStateSubject = new BehaviorSubject<RolesState>({
    rolesForProduct: [],
    activeRole: null,
  });

  activeRole$ = this.rolesStateSubject.pipe(
    map((state) => state.activeRole as string),
    filter((x) => !!x)
  );

  roles$ = this.rolesStateSubject.pipe(
    map((state) => state.rolesForProduct),
    filter((x) => x.length > 0)
  );

  constructor(
    private apiClient: ApiClient,
    private bffUserService: BffUserService
  ) {
    combineLatest({
      productType: this.getProductType(),
      roles: this.bffUserService.roles$,
    })
      .pipe(take(1))
      .subscribe(({ productType, roles: userRoles }) => {
        const possibleRolesForProduct =
          this.getPossibleRolesForProduct(productType);

        const rolesForProduct = userRoles.filter((r) =>
          possibleRolesForProduct.includes(r)
        );

        const activeRole = this.getHighestRole(rolesForProduct);

        this.rolesStateSubject.next({
          rolesForProduct: rolesForProduct,
          activeRole: activeRole,
        });
      });
  }

  selectRole(role: string) {
    const currentState = this.rolesStateSubject.value;
    if (currentState.rolesForProduct.includes(role)) {
      this.rolesStateSubject.next({
        ...currentState,
        activeRole: role,
      });
    }
  }

  private getProductType() {
    const url = productTypeUrl();
    return this.apiClient.get<ProductType>(url);
  }

  private getPossibleRolesForProduct(productType: ProductType) {
    if (productType === ProductType.ABC) {
      return ['CanvasAdmin', 'ActivityBasedCreditProjectManager'];
    } else if (productType === ProductType.Skoot) {
      return ['CanvasAdmin', 'SkootOperationManager'];
    } else {
      return ['CanvasAdmin'];
    }
  }

  private getHighestRole(roles: string[]): string | null {
    if (roles.includes('CanvasAdmin')) {
      return 'CanvasAdmin';
    } else if (roles.length > 0) {
      return roles[0];
    } else {
      return null;
    }
  }
}
